import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../api';

const CartContext = createContext();

// Unique key per product+variant combo
const itemKey = (productId, variantId) => `${productId}-${variantId || ''}`;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });

  const save = (newItems) => {
    setItems(newItems);
    localStorage.setItem('cart', JSON.stringify(newItems));
  };

  // variant is optional: { id, attributes, price_adjustment, stock_quantity }
  const addItem = useCallback((product, qty = 1, variant = null) => {
    setItems(prev => {
      const key = itemKey(product.id, variant?.id);
      const existing = prev.find(i => itemKey(i.product.id, i.variant?.id) === key);
      const effectivePrice = product.price + (variant?.price_adjustment || 0);
      const maxStock = variant?.stock_quantity ?? product.stock_quantity ?? 99;
      const currentQty = existing ? existing.quantity : 0;
      const newQty = Math.min(currentQty + qty, maxStock);
      if (newQty <= currentQty) return prev; // already at max stock
      let newItems;
      if (existing) {
        newItems = prev.map(i => itemKey(i.product.id, i.variant?.id) === key
          ? { ...i, quantity: newQty }
          : i);
      } else {
        newItems = [...prev, { product: { ...product, price: effectivePrice }, variant, quantity: Math.min(qty, maxStock) }];
      }
      localStorage.setItem('cart', JSON.stringify(newItems));
      return newItems;
    });
  }, []);

  const updateQuantity = useCallback((productId, qty, variantId = null) => {
    setItems(prev => {
      const key = itemKey(productId, variantId);
      const newItems = qty <= 0
        ? prev.filter(i => itemKey(i.product.id, i.variant?.id) !== key)
        : prev.map(i => itemKey(i.product.id, i.variant?.id) === key ? { ...i, quantity: qty } : i);
      localStorage.setItem('cart', JSON.stringify(newItems));
      return newItems;
    });
  }, []);

  const removeItem = useCallback((productId, variantId = null) => {
    setItems(prev => {
      const key = itemKey(productId, variantId);
      const newItems = prev.filter(i => itemKey(i.product.id, i.variant?.id) !== key);
      localStorage.setItem('cart', JSON.stringify(newItems));
      return newItems;
    });
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    localStorage.removeItem('cart');
  }, []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + i.product.price * i.quantity, 0);

  // Sync cart to server (debounced) for abandoned cart tracking
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || items.length === 0) return;
    const t = setTimeout(() => {
      const simplified = items.map(i => ({
        product_id: i.product.id, product_name: i.product.name,
        price: i.product.price, quantity: i.quantity,
        variant: i.variant ? i.variant.attributes : null
      }));
      api.post('/customers/cart', { items: simplified, subtotal }).catch(() => {});
    }, 2000); // 2s debounce
    return () => clearTimeout(t);
  }, [items]);

  return (
    <CartContext.Provider value={{ items, addItem, updateQuantity, removeItem, clearCart, totalItems, subtotal }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
